import { clamp } from 'lodash-es';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { getFootRatio, getLength } from '@/editor/math/shape/other';
import { IShapeSeg2d } from '@/editor/types/shape';

/** 拖动控制柄缩放时的尺寸限制，单位 mm */
export interface IScaleLimit {
  /** 宽/厚（圆柱为直径）允许的最小值 */
  min: number;
  /** 宽/厚（圆柱为直径）允许的最大值 */
  max: number;
  /** 等比缩放时按同一系数联动、也要受限的附加尺寸（家具高度） */
  linkedSize?: number;
  /** 拖动方向上允许的最大位移，门窗用来保证不越过墙端点 */
  maxOffset?: number;
}

/** 参与限制的一个尺寸 */
export interface IScaleDimension {
  /** 拖动开始时的值 */
  value: number;
  /** 每单位 offset 引起的变化量，为 0 表示该尺寸不随本次拖动改变 */
  rate: number;
}

/**
 * 把拖动位移收敛到「所有尺寸都落在 [min, max] 内」且不超过 maxOffset 的区间。
 *
 * 区间端点与 0 取 min/max，是为了不把本就越界的存量数据强行拉回区间内——
 * 否则按下鼠标的瞬间尺寸就会突变。越界数据只保证拖动不会让它更越界。
 */
export function clampScaleOffset(offset: number, dimensions: IScaleDimension[], limit: IScaleLimit): number {
  const { min, max, maxOffset } = limit;

  let lower = -Infinity;
  let upper = Infinity;

  for (const { value, rate } of dimensions) {
    if (rate === 0) continue;

    const a = (min - value) / rate;
    const b = (max - value) / rate;

    lower = Math.max(lower, Math.min(Math.min(a, b), 0));
    upper = Math.min(upper, Math.max(Math.max(a, b), 0));
  }

  if (maxOffset !== undefined) upper = Math.min(upper, maxOffset);

  if (lower > upper) return upper;

  return clamp(offset, lower, upper);
}

/**
 * 门窗沿拖动方向到墙端点的剩余可拉伸距离。
 *
 * 门窗只有左右两个控制柄，缩放时另一端不动，offset 恰好等于被拖那一端的位移量，
 * 因此该余量可以直接作为 offset 的上限。
 *
 * 弧形墙按弧长参数化，而门窗宽度是弦长，比较结果偏保守（略早停住）。
 */
export function getWindowScaleRoom(params: {
  seg: IShapeSeg2d;
  /** 门窗中心 */
  center: IPoint2d;
  /** 门窗宽度 */
  width: number;
  /** 被拖动那一侧的单位方向（沿墙） */
  direction: IPoint2d;
}): number {
  const { seg, center, width, direction } = params;

  const segLength = getLength(seg);
  if (segLength === 0) return 0;

  const movingEnd = {
    x: center.x + (direction.x * width) / 2,
    y: center.y + (direction.y * width) / 2
  };

  const centerParam = getFootRatio(seg, center) * segLength;
  const movingEndParam = getFootRatio(seg, movingEnd) * segLength;

  const room = movingEndParam > centerParam ? segLength - movingEndParam : movingEndParam;

  return Math.max(room, 0);
}
